import type {
  Cyclabilite,
  FranceVeloResponse,
} from '@dolcevia/types/franceVeloResponse.d';
import type {
  Analysis,
  Geometry,
  OpenRouteResponse,
  Steepness,
} from '@dolcevia/types/openRouteResponse.d';
//@ts-ignore
import polyline from '@mapbox/polyline';
import type {
  CoordinatesParamsType,
  StepsSelectionParamsType,
} from '../../searchParams';
import {
  cyclableIds,
  highIds,
  mediumIds,
  nonroadSurfaceIds,
  platIds,
  roadIds,
  roadSurfaceIds,
  slowIds,
} from './roads.constants';

const getCyclingType = (bikeType?: string, travelMode?: string) => {
  if (travelMode === 'dolcevia') return 'dolcevia';
  if (travelMode === 'road') {
    return 'cycling-road';
  }
  if (travelMode === 'off-road') {
    return 'cycling-mountain';
  }
  if (travelMode === 'tourist') {
    switch (bikeType) {
      case 'VAE':
        return 'cycling-electric';
      default:
        return 'cycling-regular';
    }
  }
  return 'cycling-regular';
};

const groupSurface = (surface: Steepness): Analysis[] => {
  const road = {
    title: 'Goudronée',
    value: Math.round(
      surface.summary
        .filter((summary) => roadSurfaceIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const nonRoad = {
    title: 'Non goudronée',
    value: Math.round(
      surface.summary
        .filter((summary) => nonroadSurfaceIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const others = {
    title: 'Inconnu',
    value: Math.round(
      surface.summary
        .filter((summary) => summary.value === 0)
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };

  return [road, nonRoad, others].toSorted((a, b) => b.value - a.value);
};

const groupStepness = (stepness: Steepness): Analysis[] => {
  const plat = {
    title: 'Plat ou descente',
    value: Math.round(
      stepness.summary
        .filter((summary) => platIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const slow = {
    title: 'Légère montée',
    value: Math.round(
      stepness.summary
        .filter((summary) => slowIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const medium = {
    title: 'Montée modérée',
    value: Math.round(
      stepness.summary
        .filter((summary) => mediumIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const high = {
    title: 'Montée importante',
    value: Math.round(
      stepness.summary
        .filter((summary) => highIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };

  return [plat, slow, medium, high].toSorted((a, b) => b.value - a.value);
};

const groupWayType = (wayType: Steepness): Analysis[] => {
  const road = {
    title: 'Route',
    value: Math.round(
      wayType?.summary
        .filter((summary) => roadIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const cyclable = {
    title: 'Piste cyclable',
    value: Math.round(
      wayType?.summary
        .filter((summary) => cyclableIds.includes(summary.value))
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };
  const others = {
    title: 'Inconnu',
    value: Math.round(
      wayType?.summary
        .filter(
          (summary) => ![...cyclableIds, ...roadIds].includes(summary.value),
        )
        .reduce((acc, summary) => acc + summary.distance, 0),
    ),
  };

  return [road, cyclable, others].toSorted((a, b) => b.value - a.value);
};

const convertToAnalysis = (
  geoJson?: OpenRouteResponse,
): OpenRouteResponse['metadata']['roadAnalysis'] => {
  if (!geoJson?.features) return undefined;
  return {
    surface: groupSurface(geoJson.features[0].properties.extras.surface),
    steepness: groupStepness(geoJson.features[0].properties.extras.steepness),
    waytypes: groupWayType(geoJson.features[0].properties.extras.waytypes),
  };
};

const convertFranceVeloToOpenRoute = (
  data?: FranceVeloResponse,
): OpenRouteResponse | undefined => {
  if (!data) return undefined;
  const result =
    data?.routes?.find((route) => route.type === 'recommande') ??
    data.routes?.[0];
  if (!result) return undefined;
  const geojson = polyline.toGeoJSON(result.geometry) as Geometry;
  return {
    type: 'FeatureCollection',
    bbox: [],
    features: [
      {
        bbox: [],
        type: 'Feature',
        properties: {
          ascent: 0,
          descent: 0,
          segments: result.legs.map((leg) => ({
            distance: leg.distance ?? 0,
            duration: leg.duration,
            steps: leg.steps.map((step) => ({
              distance: step.distance ?? 0,
              duration: step.duration,
              type: 0,
              instruction: step.instruction,
              name: step.name ?? '',
              way_points: step.maneuver.location,
            })),
            ascent: 0,
            descent: 0,
          })),
          extras: {
            surface: {
              values: [],
              summary: [],
            },
            waytypes: { values: [], summary: [] },
            steepness: { values: [], summary: [] },
            suitability: { values: [], summary: [] },
          },
          summary: {
            distance: result.distance ?? 0,
            duration: result.duration,
          },
          way_points: [],
        },
        geometry: geojson,
      },
    ],
    metadata: {
      roadAnalysis: {
        surface: [
          {
            title: 'Goudronée',
            value: result.metrics.surfaces?.lisse?.distance ?? 0,
          },
          {
            title: 'Non goudronée',
            value: result.metrics.surfaces?.rugueux?.distance ?? 0,
          },
          {
            title: 'Inconnu',
            value: result.metrics.surfaces?.inconnu?.distance ?? 0,
          },
        ],
        steepness: [
          {
            title: 'Plat ou descente',
            value:
              result.distance -
              (result.metrics?.pushingBike?.distance ?? 0) -
              (result.metrics.surfaces?.accidente?.distance ?? 0),
          },
          {
            title: 'Montée modérée',
            value: result.metrics.surfaces?.accidente?.distance ?? 0,
          },
          {
            title: 'Montée importante',
            value: result.metrics.pushingBike?.distance ?? 0,
          },
        ].toSorted((a, b) => b.value - a.value),
        waytypes: Object.keys(result.metrics.cyclabilite ?? {})
          .map((key) => ({
            title: key,
            value:
              result.metrics.cyclabilite?.[key as keyof Cyclabilite]?.distance,
          }))

          .toSorted((a, b) => (b.value ?? 0) - (a.value ?? 0))
          .filter((el) => el) as Analysis[],
      },
    },
  };
};

export const getDisplayedSteps = (
  pathName: string,
  coordinates: CoordinatesParamsType,
  stepsSelection: StepsSelectionParamsType | null,
) => {
  return pathName === '/recap'
    ? coordinates
        ?.map((coordinate, index) => {
          return {
            coordinates: [coordinate.lng, coordinate.lat],
            subSteps: stepsSelection?.filter(
              (step) =>
                step.stepIndex === index &&
                (step.habitation.length > 0 || step.interest.length > 0),
            ),
          };
        })
        .flatMap((step) => {
          if (!step.subSteps) return [step.coordinates];
          const subSteps = step.subSteps.flatMap((subStep) => {
            const habitationCoordinates =
              subStep.habitation.length > 0
                ? [subStep.habitation[0].lng, subStep.habitation[0].lat]
                : undefined;
            const interestCoordinates =
              subStep.interest.length > 0
                ? [subStep.interest[0].lng, subStep.interest[0].lat]
                : undefined;
            const activityCoordinates =
              subStep.activity.length > 0
                ? [subStep.activity[0].lng, subStep.activity[0].lat]
                : undefined;
            const result = [
              habitationCoordinates,
              activityCoordinates,
              interestCoordinates,
            ];
            return result.filter((coordinates) => !!coordinates);
          });
          return [step.coordinates, ...subSteps];
        })
    : coordinates?.map((coordinate) => [coordinate.lng, coordinate.lat]);
};

export { getCyclingType, convertToAnalysis, convertFranceVeloToOpenRoute };
